// Phase SA-8 — Filter pill row (outcome / verdict / time).
//
// Composes against .sa-filter-row + .sa-filter-pill from SA-3.

import type {
  HistoryFilters,
  OutcomeFilter,
  TimeRangeFilter,
  VerdictFilter,
} from "../../slips/autopsyHistoryHelpers.ts";

const OUTCOMES: ReadonlyArray<{ value: OutcomeFilter; label: string }> = [
  { value: "all", label: "All outcomes" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "partial", label: "Partial" },
];

const VERDICTS: ReadonlyArray<{ value: VerdictFilter; label: string }> = [
  { value: "all", label: "All verdicts" },
  { value: "process_correct", label: "Process correct" },
  { value: "below_threshold", label: "Below threshold" },
  { value: "model_error", label: "Model error" },
];

const TIMES: ReadonlyArray<{ value: TimeRangeFilter; label: string }> = [
  { value: "all", label: "All time" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

export interface AutopsyHistoryFiltersProps {
  value: HistoryFilters;
  onChange: (next: HistoryFilters) => void;
}

export function AutopsyHistoryFilters({
  value,
  onChange,
}: AutopsyHistoryFiltersProps): JSX.Element {
  return (
    <div
      className="sa-filter-row"
      role="group"
      aria-label="History filters"
      data-testid="sa-history-filters"
    >
      <FilterGroup
        groupLabel="Outcome"
        options={OUTCOMES}
        active={value.outcome}
        onSelect={(v) => onChange({ ...value, outcome: v })}
        testIdPrefix="outcome"
      />
      <span style={{ color: "var(--sa-fg-muted)" }}>·</span>
      <FilterGroup
        groupLabel="Verdict"
        options={VERDICTS}
        active={value.verdict}
        onSelect={(v) => onChange({ ...value, verdict: v })}
        testIdPrefix="verdict"
      />
      <span style={{ color: "var(--sa-fg-muted)" }}>·</span>
      <FilterGroup
        groupLabel="Time"
        options={TIMES}
        active={value.timeRange}
        onSelect={(v) => onChange({ ...value, timeRange: v })}
        testIdPrefix="time"
      />
    </div>
  );
}

interface FilterGroupProps<T extends string> {
  groupLabel: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  active: T;
  onSelect: (v: T) => void;
  testIdPrefix: string;
}

function FilterGroup<T extends string>({
  options,
  active,
  onSelect,
  testIdPrefix,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <>
      {options.map((o) => {
        const isActive = o.value === active;
        return (
          <button
            key={o.value}
            type="button"
            className="sa-filter-pill"
            data-active={isActive || undefined}
            data-testid={`sa-filter-${testIdPrefix}-${o.value}`}
            onClick={() => onSelect(o.value)}
            aria-pressed={isActive}
          >
            {o.label}
          </button>
        );
      })}
    </>
  );
}
